import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Loader2, Send, Sparkles, LogOut, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Connect = ({ st, onDisconnect }) => {
  const { t } = useI18n();
  const { pathname, search } = useLocation();
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/youtube/connect", { params: { next: pathname + search } });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  if (!st.configured) return <p className="text-xs text-amber-300 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3" data-testid="publish-not-configured">{t("pub.not_configured")}</p>;
  if (st.connected) return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid="publish-channel">
      {st.channel.thumb ? <img src={st.channel.thumb} alt="" className="w-6 h-6 rounded-full" /> : <Youtube className="w-5 h-5 text-[#ff0033]" />}
      <span className="text-xs text-white flex-1 truncate">{st.channel.title}</span>
      <button onClick={onDisconnect} className="text-[10px] text-slate-400 hover:text-rose-400 flex items-center gap-1" data-testid="publish-disconnect-btn"><LogOut className="w-3 h-3" />{t("pub.disconnect")}</button>
    </div>
  );
  return (
    <button onClick={connect} disabled={busy} data-testid="publish-connect-btn" className="w-full rounded-full py-3 text-sm font-semibold text-white bg-[#ff0033] hover:bg-[#e0002d] transition-colors flex items-center justify-center gap-2">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Youtube className="w-4 h-4" />}{t("pub.connect")}
    </button>
  );
};

export const PublishDialog = ({ clip, open, onOpenChange, onPublished }) => {
  const { t } = useI18n();
  const [st, setSt] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadStatus = () => api.get("/integrations/youtube/status").then((r) => setSt(r.data)).catch(() => setSt({ configured: false }));
  useEffect(() => {
    if (!open || !clip) return;
    setForm(null);
    setSt(null);
    loadStatus();
  }, [open, clip?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !clip || !st?.connected || form) return;
    api.post(`/clips/${clip.id}/publish/suggest`, { platform: "youtube" })
      .then((r) => setForm({ ...r.data, hashtags: r.data.hashtags.join(" "), privacy: "public" }))
      .catch(() => setForm({ title: clip.title, description: clip.description || "", hashtags: (clip.hashtags || []).join(" "), privacy: "public" }));
  }, [open, st?.connected]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const disconnect = async () => { await api.delete("/integrations/youtube").catch(() => {}); loadStatus(); };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clips/${clip.id}/publish`, { platform: "youtube", title: form.title, description: form.description,
        hashtags: form.hashtags.split(/\s+/).filter(Boolean), privacy: form.privacy });
      toast.success(t("pub.started"));
      onPublished?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const inp = "w-full rounded-lg bg-[#070912] border border-white/10 px-3 py-2 text-sm text-white focus:border-[#ff0033]/60 outline-none transition-colors";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#07070c]/95 backdrop-blur-xl border-white/10" data-testid="publish-dialog">
        <DialogTitle className="font-display text-white flex items-center gap-2"><Send className="w-4 h-4 text-[#ff0033]" />{t("pub.title")}</DialogTitle>
        <DialogDescription className="text-xs text-slate-400">{t("pub.desc")}</DialogDescription>
        <div className="flex gap-2">
          <span className="flex-1 rounded-full py-1.5 text-xs font-semibold text-center bg-[#ff0033]/15 border border-[#ff0033]/50 text-white flex items-center justify-center gap-1.5" data-testid="publish-tab-youtube"><Youtube className="w-3.5 h-3.5 text-[#ff0033]" />YouTube Shorts</span>
          <span className="flex-1 rounded-full py-1.5 text-xs text-center border border-white/10 text-slate-500 flex items-center justify-center gap-1.5" data-testid="publish-tab-tiktok"><Clock className="w-3.5 h-3.5" />TikTok · {t("pub.soon")}</span>
        </div>
        {!st ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /> : <Connect st={st} onDisconnect={disconnect} />}
        {st?.connected && (form ? (
          <div className="space-y-3">
            <div className="text-[10px] text-[#3ad7ff] flex items-center gap-1"><Sparkles className="w-3 h-3" />{t("pub.ai_filled")}</div>
            <label className="block text-[11px] text-slate-500">{t("pub.f_title")}
              <input value={form.title} maxLength={100} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="publish-title-input" /></label>
            <label className="block text-[11px] text-slate-500">{t("pub.f_desc")}
              <textarea value={form.description} rows={3} onChange={(e) => set("description", e.target.value)} className={inp} data-testid="publish-desc-input" /></label>
            <label className="block text-[11px] text-slate-500">{t("pub.f_tags")}
              <input value={form.hashtags} onChange={(e) => set("hashtags", e.target.value)} className={`${inp} font-mono text-[#3ad7ff]`} data-testid="publish-tags-input" /></label>
            <Select value={form.privacy} onValueChange={(v) => set("privacy", v)}>
              <SelectTrigger className="bg-[#070912] border-white/10 text-white" data-testid="publish-privacy-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["public", "unlisted", "private"].map((p) => <SelectItem key={p} value={p} data-testid={`publish-privacy-${p}`}>{t(`pub.p_${p}`)}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={send} disabled={busy || !form.title.trim()} data-testid="publish-submit-btn" className="cx-btn w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{t("pub.send")}
            </button>
          </div>
        ) : <div className="text-xs text-slate-400 flex items-center gap-2" data-testid="publish-suggest-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t("pub.writing")}</div>)}
      </DialogContent>
    </Dialog>
  );
};
